<template>
  <div class="mask-detail-page" v-if="!notFound">
    <!-- Breadcrumb -->
    <nav class="breadcrumb" aria-label="Breadcrumb">
      <ol itemscope itemtype="https://schema.org/BreadcrumbList">
        <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <router-link to="/" itemprop="item">
            <span itemprop="name">{{ $t('Home') }}</span>
          </router-link>
          <meta itemprop="position" content="1" />
        </li>
        <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <router-link to="/mask" itemprop="item">
            <span itemprop="name">{{ $t('Word Cloud Shapes') }}</span>
          </router-link>
          <meta itemprop="position" content="2" />
        </li>
        <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <span itemprop="name">{{ mask.name }}</span>
          <meta itemprop="position" content="3" />
        </li>
      </ol>
    </nav>

    <!-- Hero Section -->
    <section class="mask-hero">
      <figure
        class="mask-figure"
        itemscope
        itemtype="https://schema.org/ImageObject"
      >
        <img
          :src="mask.pre_image_remote_url"
          :alt="`${mask.name} word cloud shape mask template`"
          :title="`${mask.name} — Word Cloud Shape Mask`"
          class="mask-hero-image"
          width="800"
          height="800"
          loading="eager"
          decoding="async"
          itemprop="contentUrl"
          @error="onImageError"
        />
        <meta itemprop="name" :content="`${mask.name} Word Cloud Shape`" />
        <meta itemprop="description" :content="metaDescription" />
        <figcaption class="mask-caption" itemprop="caption">
          {{ mask.name }} — {{ $t('Word Cloud Shape Mask') }}
        </figcaption>
      </figure>
    </section>

    <!-- Content Section -->
    <section class="mask-content">
      <h1 class="mask-title">{{ mask.name }} {{ $t('Word Cloud Shape Mask') }}</h1>
      <p class="mask-description">
        {{ metaDescription }}
      </p>
      <p class="mask-use-case">
        {{ $t('Perfect for creating') }}
        {{ mask.name.toLowerCase() }}{{ $t('-themed word clouds, visual branding, social media graphics, presentations, and personalized word art.') }}
      </p>

      <!-- CTA -->
      <div class="mask-cta">
        <router-link
          :to="`/?mask=${encodeURIComponent(mask.mask || mask.pre_image_remote_url)}`"
          class="cta-button"
        >
          {{ $t('Create Word Cloud with') }} {{ mask.name }} {{ $t('Shape') }}
        </router-link>
        <router-link to="/mask" class="cta-secondary">
          ← {{ $t('Browse All Shapes') }}
        </router-link>
      </div>
    </section>

    <!-- Related Masks -->
    <section class="related-masks" v-if="relatedMasks.length > 0">
      <h2 class="related-title">{{ $t('Explore More Word Cloud Shapes') }}</h2>
      <div class="related-grid">
        <router-link
          v-for="rel in relatedMasks"
          :key="rel.slug"
          :to="`/mask/${rel.slug}`"
          class="related-card"
        >
          <div class="related-image-wrapper">
            <img
              :src="rel.pre_image_remote_url"
              :alt="`${rel.name} word cloud shape mask`"
              loading="lazy"
              decoding="async"
            />
          </div>
          <span class="related-name">{{ rel.name }}</span>
        </router-link>
      </div>
    </section>

    <!-- JSON-LD Structured Data -->
    <component :is="'script'" type="application/ld+json">
      {{ breadcrumbJsonLd }}
    </component>
    <component :is="'script'" type="application/ld+json">
      {{ imageObjectJsonLd }}
    </component>
  </div>

  <!-- 404 State -->
  <div class="mask-not-found" v-else>
    <el-icon :size="64" class="not-found-icon"><PictureFilled /></el-icon>
    <h1>{{ $t('Shape Not Found') }}</h1>
    <p>{{ $t('The word cloud shape you\'re looking for doesn\'t exist or may have been removed.') }}</p>
    <router-link to="/mask" class="cta-button">
      {{ $t('Browse All Shapes') }}
    </router-link>
  </div>
</template>

<script>
import axios from 'axios'
import { useSEO } from '@/composables/useSEO'
import { PictureFilled } from '@element-plus/icons-vue'

const MASK_METADATA_URL = '/mask-metadata.json'
const RELATED_COUNT = 6
const CANONICAL_BASE = 'https://wordcloudstudio.com'

export default {
  name: 'MaskDetail',
  components: { PictureFilled },

  data() {
    return {
      mask: {
        id: null,
        name: '',
        mask: '',
        pre_image_remote_url: ''
      },
      notFound: false,
      imageError: false,
      allMasks: {}
    }
  },

  computed: {
    slug() {
      return this.$route.params.slug || ''
    },

    metaDescription() {
      const name = this.mask.name || this.slug
      return `Create stunning ${name.toLowerCase()} word cloud shape designs online. Free ${name.toLowerCase()} mask template for word art — perfect for visual branding, presentations, and personalized gifts.`
    },

    metaKeywords() {
      const name = (this.mask.name || this.slug).toLowerCase()
      return `${name} word cloud, ${name} shape mask, ${name} word art template, ${name} tag cloud shape, word cloud ${name} silhouette`
    },

    relatedMasks() {
      // Pick 6 other masks to show as related content
      const entries = Object.entries(this.allMasks)
        .filter(([s]) => s !== this.slug)
      const shuffled = entries.sort(() => 0.5 - Math.random())
      return shuffled.slice(0, RELATED_COUNT).map(([s, data]) => ({
        slug: s,
        name: data.name,
        pre_image_remote_url: data.pre_image_remote_url
      }))
    },

    breadcrumbJsonLd() {
      const name = this.mask.name || this.slug
      return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: CANONICAL_BASE },
          { '@type': 'ListItem', position: 2, name: 'Word Cloud Shapes', item: `${CANONICAL_BASE}/mask` },
          { '@type': 'ListItem', position: 3, name: `${name} Shape`, item: `${CANONICAL_BASE}/mask/${encodeURIComponent(this.slug)}` }
        ]
      }, null, 2)
    },

    imageObjectJsonLd() {
      const name = this.mask.name || this.slug
      return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        '@id': `#mask-${this.slug}`,
        name: `${name} Word Cloud Shape Mask`,
        description: this.metaDescription,
        contentUrl: this.mask.pre_image_remote_url,
        thumbnailUrl: this.mask.pre_image_remote_url,
        representativeOfPage: true,
        creator: {
          '@type': 'Organization',
          name: 'WordCloudStudio'
        }
      }, null, 2)
    }
  },

  methods: {
    onImageError() {
      this.imageError = true
    },

    async loadMaskData() {
      try {
        // Fetch the prebuilt metadata map
        const response = await axios.get(MASK_METADATA_URL)
        this.allMasks = response.data || {}

        // Look up the requested slug
        const maskData = this.allMasks[this.slug]

        if (!maskData) {
          this.notFound = true
          return
        }

        this.mask = {
          id: maskData.id,
          name: maskData.name,
          mask: maskData.mask,
          pre_image_remote_url: maskData.pre_image_remote_url
        }

        // Set SEO meta tags
        this.applySEO()

        // GA event
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'mask_detail_view', {
            mask_name: this.mask.name,
            mask_slug: this.slug
          })
        }

      } catch (err) {
        console.error('Failed to load mask metadata:', err)
        this.notFound = true
      }
    },

    applySEO() {
      const name = this.mask.name || this.slug
      useSEO({
        title: `${name} Word Cloud Shape Mask | WordCloudStudio`,
        description: this.metaDescription,
        keywords: this.metaKeywords,
        canonical: `/mask/${encodeURIComponent(this.slug)}`,
        ogTitle: `${name} Word Cloud Shape Mask | WordCloudStudio`,
        ogDescription: this.metaDescription,
        ogImage: this.mask.pre_image_remote_url || 'https://wordcloudstudio.com/wcdw.png',
        ogType: 'website',
        twitterTitle: `${name} Word Cloud Shape | WordCloudStudio`,
        twitterDescription: this.metaDescription,
        twitterImage: this.mask.pre_image_remote_url || 'https://wordcloudstudio.com/wcdw.png'
      })
    }
  },

  watch: {
    // Re-fetch if user navigates between mask detail pages
    '$route.params.slug': function (newSlug) {
      // The slug computed property has already updated
      const maskData = this.allMasks[newSlug]
      if (maskData) {
        this.mask = {
          id: maskData.id,
          name: maskData.name,
          mask: maskData.mask,
          pre_image_remote_url: maskData.pre_image_remote_url
        }
        this.notFound = false
        this.applySEO()
      } else if (Object.keys(this.allMasks).length > 0) {
        // Metadata already loaded, slug not found
        this.notFound = true
      } else {
        // Metadata not loaded yet, reload
        this.loadMaskData()
      }
    }
  },

  mounted() {
    this.loadMaskData()
  }
}
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────── */
.mask-detail-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px 64px;
}

/* ── Breadcrumb ──────────────────────────────────────── */
.breadcrumb {
  margin-bottom: 24px;
  font-size: 0.875rem;
  color: #64748b;
}

.breadcrumb ol {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.breadcrumb li {
  display: flex;
  align-items: center;
}

.breadcrumb li + li::before {
  content: '›';
  margin: 0 8px;
  color: #94a3b8;
}

.breadcrumb a {
  color: #6366f1;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* ── Hero ────────────────────────────────────────────── */
.mask-hero {
  text-align: center;
  margin-bottom: 32px;
}

.mask-figure {
  margin: 0;
}

.mask-hero-image {
  max-width: 400px;
  width: 100%;
  height: auto;
  aspect-ratio: 1;
  object-fit: contain;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.mask-caption {
  margin-top: 12px;
  font-size: 0.9rem;
  color: #94a3b8;
}

/* ── Content ─────────────────────────────────────────── */
.mask-content {
  text-align: center;
  margin-bottom: 48px;
}

.mask-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 12px;
  line-height: 1.3;
}

.mask-description {
  max-width: 640px;
  margin: 0 auto 12px;
  font-size: 1.05rem;
  color: #475569;
  line-height: 1.7;
}

.mask-use-case {
  max-width: 640px;
  margin: 0 auto 24px;
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.6;
}

/* ── CTA ─────────────────────────────────────────────── */
.mask-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.35);
}

.cta-secondary {
  color: #6366f1;
  font-size: 0.95rem;
  text-decoration: none;
}

.cta-secondary:hover {
  text-decoration: underline;
}

/* ── Related Masks ───────────────────────────────────── */
.related-masks {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid #e2e8f0;
}

.related-title {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 20px;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
}

.related-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
}

.related-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.related-image-wrapper {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  background: #f8fafc;
  margin-bottom: 8px;
}

.related-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.related-name {
  font-size: 0.8rem;
  color: #475569;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ── 404 ─────────────────────────────────────────────── */
.mask-not-found {
  text-align: center;
  padding: 80px 20px;
}

.not-found-icon {
  color: #cbd5e1;
  margin-bottom: 16px;
}

.mask-not-found h1 {
  font-size: 1.5rem;
  color: #1a202c;
  margin: 0 0 8px;
}

.mask-not-found p {
  color: #64748b;
  margin: 0 0 24px;
}
</style>
